import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useWordPress } from './WordPressContext';
import { adminApi } from '../config/wpApi';

const WpAdminContext = createContext({});

export function WpAdminProvider({ children }) {
  const { isInWordPress, wpRestSession } = useWordPress();
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  const refreshStatus = useCallback(async () => {
    if (!isInWordPress || !wpRestSession) return null;
    setStatusLoading(true);
    try {
      const data = await adminApi.getStatus();
      setStatus(data);
      return data;
    } catch (err) {
      console.error('WpAdminContext: failed to fetch status', err);
      return null;
    } finally {
      setStatusLoading(false);
    }
  }, [isInWordPress, wpRestSession]);

  // Fetch status when WP session becomes available
  useEffect(() => {
    if (isInWordPress && wpRestSession && !status) {
      refreshStatus();
    }
  }, [isInWordPress, wpRestSession, status, refreshStatus]);

  // Also listen for session-ready events (from WordPressAuthBridge)
  useEffect(() => {
    const handler = () => refreshStatus();
    window.addEventListener('vfr-wp-session-ready', handler);
    return () => window.removeEventListener('vfr-wp-session-ready', handler);
  }, [refreshStatus]);

  const isLicenseActive = status?.license?.status === 'active';
  const isSetupComplete = status?.setup_complete ?? false;
  const pluginVersion = status?.plugin_version ?? '';

  return (
    <WpAdminContext.Provider value={{
      status,
      statusLoading,
      refreshStatus,
      isLicenseActive,
      isSetupComplete,
      pluginVersion,
      isInWordPress,
    }}>
      {children}
    </WpAdminContext.Provider>
  );
}

export function useWpAdmin() {
  return useContext(WpAdminContext);
}
